import React, { useState, useEffect, useMemo } from 'react';
import _ from 'lodash/fp';
import { useNavigate, useParams, useSearchParams, Navigate } from 'react-router-dom';
import { Container, Form, Tab, Tabs, Card } from 'react-bootstrap';
import Select from 'react-select';
import Footer from 'components/Footer';
import useSoilAnalyses from './useSoilAnalyses';
import SoilAnalysesForm from './SoilAnalysesForm';
import {
  generateAvailabilityReport,
  requirementCalculation,
  calculateFertilizerContribution,
  computeDeficits,
  buildFertilizerPlan,
  summarizePlan,
  fertilizers,
  fertilizerPrices
} from 'engine';
import tableGenerator from 'utils/tableGenerator.mjs';
import DATable from 'components/DATable';
import { predictYield } from 'utils/backendClient';
import QgisMap from '../QGISConfig.js';

export function SoilAnalysesPage() {
  const { soilAnalysesId } = useParams();
  const [searchParams, ] = useSearchParams();
  const sections = useMemo(
    () => {
      const sections = searchParams.getAll('section');
      return sections.length > 0 ? sections : undefined;
    },
    [searchParams]);
  const { isLoading, data, update } = useSoilAnalyses();
  const [ soil_analyses, setSoilAnalyses ] = useState(undefined);

  // eslint-disable-next-line eqeqeq
  const getSoilAnalysesFromList = () => data?.find((elem) => elem?.id == soilAnalysesId);
  const backendSoilAnalyses = useMemo(getSoilAnalysesFromList, [data, soilAnalysesId]);

  const hasChanged = useMemo(
    () =>
      !_.isUndefined(backendSoilAnalyses) &&
      !_.isUndefined(soil_analyses) &&
      !_.isEqual(soil_analyses, backendSoilAnalyses),
    [soil_analyses, backendSoilAnalyses]
  );

  useEffect(() => {
    setSoilAnalyses(backendSoilAnalyses);
  }, [backendSoilAnalyses]);

  if ( !isLoading && backendSoilAnalyses === undefined ) return <Navigate to='..' />;

  const handleSubmit = (e) => {
    e.preventDefault();
    update(soil_analyses);
  };

  const handleUndo = () => {
    setSoilAnalyses(getSoilAnalysesFromList());
  };

  return <SoilAnalysesPageLayout
    soil_analyses={soil_analyses} setSoilAnalyses={setSoilAnalyses}
    onSubmit={handleSubmit} onUndo={handleUndo}
    hasChanged={hasChanged} defaultSections={sections}/>;
}

export function NewSoilAnalysesPage() {
  const { insert } = useSoilAnalyses();
  const navigate = useNavigate();
  const [ soil_analyses, setSoilAnalyses ] = useState({});

  const hasChanged = useMemo(() => !_.isEqual(soil_analyses, {}), [soil_analyses]);

  const handleSubmit = (e) => {
    e.preventDefault();
    insert(soil_analyses)?.then((newRow) => {
      navigate({
        pathname: `../${newRow.id}`,
        search: '?section=editor',
      }, { replace: true });
    });
  };

  const handleUndo = () => {
    setSoilAnalyses({});
  };

  return <SoilAnalysesPageLayout
    soil_analyses={soil_analyses} setSoilAnalyses={setSoilAnalyses}
    onSubmit={handleSubmit} onUndo={handleUndo}
    hasChanged={hasChanged} defaultSections={['editor']}/>;
}

export const cropsConfig = [
  { label: 'Soja', value: 'soja' },
  { label: 'Trigo', value: 'trigo' },
  { label: 'Maiz', value: 'maiz' },
  { label: 'Sorgo', value: 'sorgo' },
  { label: 'Girasol', value: 'girasol' },
];

export const modelOptions = [
  { label: 'Gradient Boosting (GB)', value: 'gb' },
  { label: 'Random Forest (RF)', value: 'rf' },
  { label: 'XGBoost', value: 'xgb' },
  { label: 'Support Vector Regression (SVR)', value: 'svr' },
  { label: 'Quantile Regression', value: 'quantile' }
];

export const cordobaDepartments = [
  'Calamuchita', 'Capital', 'Colón', 'Cruz del Eje', 'General Roca',
  'General San Martín', 'Ischilín', 'Juárez Celman', 'Marcos Juárez',
  'Minas', 'Pocho', 'Presidente Roque Sáenz Peña', 'Punilla',
  'Río Cuarto', 'Río Primero', 'Río Seco', 'Río Segundo',
  'San Alberto', 'San Javier', 'San Justo', 'Santa María',
  'Sobremonte', 'Tercero Arriba', 'Totoral', 'Tulumba', 'Unión'
];

function SoilAnalysesPageLayout({
  soil_analyses,
  setSoilAnalyses,
  onSubmit,
  onUndo,
  hasChanged
}) {
  const [activeTab, setActiveTab] = useState('manual');
  const data = soil_analyses?.soil_analyses_data;
  const [performanceMode, setPerformanceMode] = useState('predicho');

  const selectedFertilizers = useMemo(() => {
    return (
      soil_analyses?.soil_analyses_data?.fertilizer_plans?.[performanceMode]?.products ?? {}
    );
  }, [soil_analyses, performanceMode]);

  const savePlanToState = (plan) => {
    const resumen = summarizePlan(plan);

    setSoilAnalyses(prev =>
      _.set(
        `soil_analyses_data.fertilizer_plans.${performanceMode}`,
        {
          products: plan,
          totalUSDha: resumen.totalUSDha,
          contribution: resumen.contribution,
        },
        prev
      )
    );
  };

  const handleCultivoChange = (value) => {
    setSoilAnalyses(prev => _.set('soil_analyses_data.selected_crop', value, prev));
  };

  const handleZonaChange = (value) => {
    setSoilAnalyses(prev => _.set('soil_analyses_data.selected_zone', value, prev));
  };

  const handleRendimientoObjetivoChange = (e) => {
    const raw = e.target.value;
    const value = raw === '' ? '' : Math.max(0, Number(raw));
    setSoilAnalyses(prev => _.set('soil_analyses_data.target_performance', value, prev));
  };

  const dataForBalance = (d) => {
    if (!d) return { result: {}, requirements: {}, balance: {} };

    const result = generateAvailabilityReport(d);
    const requirements = requirementCalculation(d);
    const balance = {};

    for (let prop in result) {
      balance[prop] = result[prop] - requirements[prop];
    }

    return { result, requirements, balance };
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    onSubmit(e);
  };

  const getFertilizationInterpretation = () => {
    if (!dataWithPerformance) return null;

    const result = generateAvailabilityReport(dataWithPerformance);
    const requirements = requirementCalculation(dataWithPerformance);
    const { deficits } = computeDeficits(result, requirements);

    const deficientElements = Object.entries(deficits)
      .filter(([_, value]) => value > 0)
      .map(([elem, value]) => ({ elem, value }));

    if (deficientElements.length === 0) {
      return 'El análisis indica que no se requieren fertilizaciones adicionales: el suelo presenta niveles adecuados de nutrientes.';
    }

    const fertilizerList = Object.entries(suggestedPlan)
      .filter(([_, dose]) => dose > 0)
      .map(([fertKey]) => fertilizers[fertKey] || fertKey);

    const nutrientDetails = deficientElements
      .map(({ elem, value }) => `${elem} (${value.toFixed(2)} kg/ha)`)
      .join(', ');

    const fertilizerNames = fertilizerList.join(', ');

    const rendimientoTxt =
      performanceMode === 'predicho'
        ? 'rendimiento potencial estimado'
        : 'rendimiento objetivo definido';

    return `En base al ${rendimientoTxt}, el suelo presenta deficiencia de ${nutrientDetails}. Se recomienda aplicar ${fertilizerNames}.`;
  };

  const [loadingPred, setLoadingPred] = useState(false);

  async function handlePrediccion() {
    const dept = soil_analyses?.soil_analyses_data?.selected_zone;
    const crop = soil_analyses?.soil_analyses_data?.selected_crop;

    if (!dept || !crop) {
      alert('Seleccioná un cultivo y una zona primero.');
      return;
    }

    setLoadingPred(true);

    try {
      console.log('📤 Enviando a backend:', {
        dept,
        crop,
        id: soil_analyses?.id
      });

      const result = await predictYield(dept, crop, soil_analyses?.id);

      setSoilAnalyses(prev =>
        _.set('soil_analyses_data.predicted_performance', result.prediccion, prev)
      );

    } catch (err) {
      alert('Error al obtener la predicción.');
    } finally {
      setLoadingPred(false);
    }
  }

  const effectivePerformance = useMemo(() => {
    if (!soil_analyses?.soil_analyses_data) return null;

    if (performanceMode === 'objetivo') {
      const raw = soil_analyses.soil_analyses_data.target_performance;

      if (raw === '' || raw === null || raw === undefined) return 0;

      const val = Number(raw);
      return Number.isNaN(val) ? 0 : val;
    }

    if (performanceMode === 'predicho') {
      const pred = Number(soil_analyses.soil_analyses_data.predicted_performance);
      return Number.isNaN(pred) ? 0 : pred / 1000;
    }

    return 0;
  }, [performanceMode, soil_analyses]);
  
  const dataWithPerformance = useMemo(() => {
    if (!data || effectivePerformance === null) return null;

    return _.set(
      'target_performance',
      effectivePerformance,
      data
    );
  }, [data, effectivePerformance]);

  const suggestedPlan = useMemo(() => {
    if (!dataWithPerformance) return {};

    const result = generateAvailabilityReport(dataWithPerformance);
    const requirements = requirementCalculation(dataWithPerformance);
    const { deficits } = computeDeficits(result, requirements);

    return buildFertilizerPlan(deficits);
  }, [dataWithPerformance]);

  const renderBalanceAndRecommendation = () => {
    if (!dataWithPerformance) return null;

    const { result, requirements, balance } = dataForBalance(dataWithPerformance);

    let contribution = {};
    let newBalance = balance;

    if (Object.keys(selectedFertilizers).length > 0) {
      const planSummary = summarizePlan(selectedFertilizers);
      contribution = planSummary?.contribution ?? {};
      newBalance = calculateFertilizerContribution(selectedFertilizers, balance).newBalance;
    }

    const table = tableGenerator(
      { result, requirements, balance: newBalance },
      [],
      false
    );

    table[0] = [
      'Elemento',
      'Disponibilidad (kg/ha)',
      'Requerimientos (kg/ha)',
      'Balance (kg/ha)',
      'Aporte (kg/ha)',
    ];

    for (let i = 1; i < table.length; i++) {
      const elemento = table[i][0];
      table[i][4] = (contribution[elemento] ?? 0).toFixed(2);
    }

    return (
      <>
        <Form.Group className='mt-5 mb-5'>
          <Form.Label className='fw-bold'>Balance de Nutrientes</Form.Label>
          {(() => {
            const { result, requirements, balance } = dataForBalance(dataWithPerformance);

            let contribution = {};
            let newBalance = balance;

            if (Object.keys(selectedFertilizers).length > 0) {
              const planSummary = summarizePlan(selectedFertilizers);
              contribution = planSummary?.contribution ?? {};
              newBalance = calculateFertilizerContribution(selectedFertilizers, balance).newBalance;
            }

            const table = tableGenerator({ result, requirements, balance: newBalance }, [], false);
            table[0] = [
              'Elemento',
              'Disponibilidad (kg/ha)',
              'Requerimientos (kg/ha)',
              'Balance (kg/ha)',
              'Aporte (kg/ha)',
            ];

            for (let i = 1; i < table.length; i++) {
              const elemento = table[i][0];
              table[i][4] = (contribution[elemento] ?? 0).toFixed(2);
            }

            return <DATable rows={table} />;
          })()}
        </Form.Group>

        <Form.Group className='mt-4'>
          {Object.keys(selectedFertilizers).length === 0 ? (
            <>
              <Form.Label className='fw-bold'>Recomendación Óptima Sugerida</Form.Label>

              {data && Object.keys(suggestedPlan).length > 0 ? (
                <>
                  <p className='text-muted mb-3'>
                    {getFertilizationInterpretation()}
                  </p>

                  <DATable
                    rows={[
                      ['Fertilizante', 'Dosis Sugerida (kg/ha)', 'Costo (USD/ha)'],
                      ...Object.entries(suggestedPlan).map(([fertKey, kgHa]) => {
                        const price = fertilizerPrices[fertKey] ?? 0;
                        const cost = (price * (Number(kgHa) || 0)).toFixed(2);
                        return [
                          fertilizers[fertKey] || fertKey,
                          kgHa.toFixed(0),
                          cost
                        ];
                      }),
                    ]}
                  />

                  <div className='mt-3'>
                    <strong>Costo Total Recomendado:</strong>{' '} USD{' '}
                    {Object.entries(suggestedPlan)
                      .reduce((acc, [fertKey, kgHa]) => {
                        const price = fertilizerPrices[fertKey] ?? 0;
                        return acc + price * (Number(kgHa) || 0);
                      }, 0)
                      .toFixed(2)}{' '} / ha
                  </div>

                  <div className='mt-3 d-flex gap-2'>
                    <button
                      type='button'
                      className='btn btn-primary'
                      onClick={() => savePlanToState(suggestedPlan)}
                      disabled={performanceMode === 'predicho' && !effectivePerformance}
                    >
                      Aplicar Recomendación Óptima
                    </button>
                  </div>
                </>
              ) : (
                <p className='text-dark'>
                  No hay datos suficientes para generar una recomendación óptima de fertilizantes.
                </p>
              )}
            </>
          ) : (
            <>
              <Form.Label className='fw-bold'>Recomendación Óptima Aplicada</Form.Label>

              <DATable
                rows={[
                  ['Fertilizante', 'Dosis (kg/ha)', 'Costo (USD/ha)', ''],
                  ...Object.entries(selectedFertilizers).map(([fertKey, kgHa]) => {
                    const isNew = fertKey.startsWith('nuevo_');
                    const price = fertilizerPrices[fertKey] ?? 0;
                    const cost = (price * (Number(kgHa) || 0)).toFixed(2);

                    return [
                      isNew ? (
                        <Select
                          classNamePrefix='custom-select'
                          options={Object.keys(fertilizers).map((k) => ({
                            value: k,
                            label: fertilizers[k],
                          }))}
                          onChange={(opt) => {
                            const updated = { ...selectedFertilizers };
                            const currentDose = updated[fertKey] ?? 0;
                            delete updated[fertKey];
                            updated[opt.value] = (updated[opt.value] ?? 0) + currentDose;
                            savePlanToState(updated);
                          }}
                          placeholder='Seleccionar'
                          menuPortalTarget={document.body}
                          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                        />
                      ) : (
                        fertilizers[fertKey] || fertKey
                      ),
                      <Form.Control
                        key={fertKey}
                        type='number'
                        min='0'
                        value={(Number(kgHa) || 0).toFixed(0)}
                        onChange={(e) => {
                          const val = Math.max(0, Number(e.target.value) || 0);
                          savePlanToState({
                            ...selectedFertilizers,
                            [fertKey]: val,
                          });
                        }}
                        onWheel={(e) => e.target.blur()}
                      />,
                      cost,
                      <button
                        key={`remove_${fertKey}`}
                        type='button'
                        className='btn btn-sm btn-outline-danger'
                        onClick={() => {
                          const updated = { ...selectedFertilizers };
                          delete updated[fertKey];
                          savePlanToState(updated);
                        }}
                      >
                        &minus;
                      </button>,
                    ];
                  }),
                  [
                    {
                      colSpan: 4,
                      content: (
                        <div className='text-center'>
                          <button
                            type='button'
                            className='btn btn-sm btn-outline-success'
                            onClick={() => {
                              const newKey = `nuevo_${Date.now()}`;
                              savePlanToState({ ...selectedFertilizers, [newKey]: 0 });
                            }}
                          >
                            + Agregar Fertilizante
                          </button>
                        </div>
                      ),
                    },
                  ],
                ]}
              />

              <div className='mt-2'>
                <strong>Costo Total Recomendado: </strong>USD{' '}
                {summarizePlan(selectedFertilizers).totalUSDha.toFixed(2)} / ha
              </div>

              <div className='mt-3 d-flex gap-2'>
                <button
                  type='button'
                  className='btn btn-outline-secondary'
                  onClick={() => savePlanToState({})}
                >
                  Volver a la Recomendación Óptima Sugerida
                </button>
              </div>
            </>
          )}
        </Form.Group>
      </>
    );
  };

  return (
    <Form onSubmit={onSubmit}>
      <Container className='page-container'>
        <Card className='mb-3 border-0 shadow-sm'>
          <Card.Body>
            <h4 className='card-title-lote'>
              {soil_analyses?.name || 'Nuevo Análisis de Suelo'}
            </h4>
          </Card.Body>
        </Card>

        <Tabs
          id='soil-analyses-tabs'
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k)}
          className='styled-tabs mb-3'
        >
          <Tab eventKey='manual' title='Carga Manual de Análisis de Suelo'> 
            <SoilAnalysesForm
              soil_analyses={soil_analyses}
              setSoilAnalyses={setSoilAnalyses}
            />
          </Tab>

          <Tab eventKey='balance' title='Balance de Nutrientes'>
            <div className='mb-5'>
              <Form.Label className='fw-bold'>Seleccionar Cultivo</Form.Label>
              <Form.Group className='d-flex flex-wrap gap-3'>
                {cropsConfig.map((crop) => (
                  <div
                    key={crop.value}
                    className={`select-card ${soil_analyses?.soil_analyses_data?.selected_crop === crop.value ? 'selected' : ''}`}
                    onClick={() => handleCultivoChange(crop.value)}
                  >
                    {crop.label}
                  </div>
                ))}
              </Form.Group>

              <Form.Group className='mt-4'>
                <Form.Label className='fw-bold'>Seleccionar Zona Geográfica</Form.Label>
                <Select
                  classNamePrefix='custom-select'
                  options={cordobaDepartments.map(d => ({ label: d, value: d }))}
                  value={cordobaDepartments
                    .map(d => ({ label: d, value: d }))
                    .find(opt => opt.value === soil_analyses?.soil_analyses_data?.selected_zone)}
                  onChange={(opt) => handleZonaChange(opt.value)}
                  placeholder='Buscar Departamento de la Provincia de Córdoba'
                  menuPortalTarget={document.body}
                  styles={{
                    menuPortal: base => ({ ...base, zIndex: 9999 })
                  }}
                />
              </Form.Group>

              <Form.Group className='mt-4'>
                <Form.Label className='fw-bold mb-2'>Modo de Rendimiento</Form.Label>

                <div className='d-flex gap-3 flex-wrap performance-selector'>
                  {/* Card: Rendimiento Predicho */}
                  <div
                    className={`perf-card ${performanceMode === 'predicho' ? 'selected' : ''}`}
                    onClick={() => setPerformanceMode('predicho')}
                  >
                    <div className='perf-title'>Predicho</div>
                    <div className='perf-desc'>Basado en Modelos de Machine Learning</div>
                  </div>

                  {/* Card: Rendimiento Objetivo */}
                  <div
                    className={`perf-card ${performanceMode === 'objetivo' ? 'selected' : ''}`}
                    onClick={() => setPerformanceMode('objetivo')}
                  >
                    <div className='perf-title'>Objetivo</div>
                    <div className='perf-desc'>Ingresado Manualmente</div>
                  </div>
                </div>
              </Form.Group>

              {performanceMode === 'objetivo' && (
                <>
                  <Form.Group className='mt-4 mb-4'>
                    <Form.Label className='fw-bold'>Rendimiento Objetivo (tn/ha)</Form.Label>
                    <div className='d-flex align-items-center gap-2'>
                      <Form.Control
                        style={{ maxWidth: 240 }}
                        type='number'
                        inputMode='decimal'
                        min='0'
                        step='0.01'
                        value={soil_analyses?.soil_analyses_data?.target_performance ?? ''}
                        onChange={handleRendimientoObjetivoChange}
                        onWheel={(e) => e.target.blur()}
                      />
                      <span className='text-muted'> tn/ha</span>
                    </div>

                    {renderBalanceAndRecommendation()}

                  </Form.Group>
                </>
              )}

              {performanceMode === 'predicho' && (
                <Form.Group className='mt-4'>
                  <div className='d-flex align-items-center gap-3 flex-wrap'>

                    {/* Botón */}
                    <button
                      type='button'
                      className='btn btn-success px-4 py-2'
                      onClick={handlePrediccion}
                      disabled={loadingPred}
                    >
                      {loadingPred ? 'Calculando...' : 'Calcular Rendimiento Potencial'}
                    </button>

                    {/* Card de rendimiento */}
                    {soil_analyses?.soil_analyses_data?.predicted_performance !== undefined && (
                      <Card className='shadow-sm border-0' style={{ minWidth: 182 }}>
                        <Card.Body className='d-flex align-items-center justify-content-between py-2'>
                          <div>
                            <div className='text-muted small'>
                              Rendimiento Potencial
                            </div>
                            <div className='fw-bold fs-4'>
                              {(Number(
                                soil_analyses.soil_analyses_data.predicted_performance
                              ) / 1000).toFixed(2)}
                              <span className='fs-6 fw-normal text-muted ms-2'>tn/ha</span>
                            </div>
                          </div>
                        </Card.Body>
                      </Card>
                    )}
                  </div>

                  {renderBalanceAndRecommendation()}
                </Form.Group>
              )}
            </div>
          </Tab>
          
          <Tab eventKey='variablesGranulometricas' title='Variables Granulométricas'>
            <div className='mb-3'>
              <Form.Label className='fw-bold'>Variables Granulométricas</Form.Label>
              <p className='text-muted mb-3'>
                Hacé click sobre el mapa para tomar Arena, Limo y Arcilla, y calculá la textura.
              </p>
            </div>

            <QgisMap
              mode='granulometria'
              granulometria={soil_analyses?.soil_analyses_data?.granulometria}
              onSaveGranulometry={(payload) => {
                setSoilAnalyses((prev) =>
                  _.set('soil_analyses_data.granulometria', payload, prev)
                );
              }}
            />
          </Tab>

          <Tab eventKey='cartasSuelo' title='Cartas de Suelo'>
            <div className='mb-3'>
              <Form.Label className='fw-bold'>Cartas de Suelo</Form.Label>
              <p className='text-muted mb-3'>
                Hacé click sobre el polígono de la carta de suelo para ver sus atributos y guardar la selección.
              </p>
            </div>

            <QgisMap
              mode='cartas_suelo'
              cartaSuelo={soil_analyses?.soil_analyses_data?.carta_suelo}
              onSaveCartaSuelo={(payload) => {
                setSoilAnalyses((prev) =>
                  _.set('soil_analyses_data.carta_suelo', payload, prev)
                );
              }}
            />
          </Tab>
        </Tabs>
        
      </Container>
      <Footer hasChanged={hasChanged} onSubmit={handleFormSubmit} onUndo={onUndo} />
    </Form>
  );
}